import { useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import { MESSAGE_MAX, contactSchema, sendContact, type ContactFormData } from '../lib/contact';

const CONTACT_MAIL = 'info@virtum.sk';

type Result = 'success' | 'error' | null;

export default function ContactForm() {
  const { t, i18n } = useTranslation();
  const uid = useId();
  const [result, setResult] = useState<Result>(null);
  const [sending, setSending] = useState(false);
  // Druhé odoslanie počas čakania sa ignoruje; ref, lebo stav sa premietne až po vykreslení.
  const sendingRef = useRef(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: '', email: '', company: '', message: '' },
  });

  const onSubmit = async (data: ContactFormData) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setResult(null);
    const ok = await sendContact(data, i18n.language);
    sendingRef.current = false;
    setSending(false);
    if (ok) {
      reset();
      setResult('success');
    } else {
      // Vyplnené polia ostávajú, aby ich návštevník nemusel písať znova.
      setResult('error');
    }
  };

  const inputClass = "w-full rounded-xl border border-white/25 bg-white/88 px-4 py-3 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:border-indigo-300 aria-[invalid=true]:border-red-400 aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-red-400 transition text-sm shadow-sm"

  // Chyba poľa: text (nie len farba) s ikonou, prepojený cez aria-describedby. Kľúč sa prekladá pri vykreslení.
  const fieldError = (name: keyof ContactFormData) => {
    const message = errors[name]?.message;
    if (!message) return null;
    return (
      <span id={`${uid}-${name}-err`} className="flex items-start gap-1.5 text-[13px] leading-snug text-red-200">
        <span aria-hidden="true" className="mt-px inline-grid h-4 w-4 flex-none place-items-center rounded-full bg-red-200 text-[11px] font-bold text-red-900">!</span>
        <span>{t(message, { max: MESSAGE_MAX })}</span>
      </span>
    );
  };
  const fieldProps = (name: keyof ContactFormData, required: boolean) => ({
    id: `${uid}-${name}`,
    'aria-invalid': !!errors[name],
    'aria-describedby': errors[name] ? `${uid}-${name}-err` : undefined,
    'aria-required': required || undefined,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="grid gap-2 content-start">
          <label htmlFor={`${uid}-name`} className="text-xs font-medium text-slate-200 uppercase tracking-wider">{t('contact.name')}</label>
          <input
            type="text"
            autoComplete="name"
            {...register('name')}
            {...fieldProps('name', true)}
            className={inputClass}
          />
          {fieldError('name')}
        </div>

        <div className="grid gap-2 content-start">
          <label htmlFor={`${uid}-email`} className="text-xs font-medium text-slate-200 uppercase tracking-wider">{t('contact.email')}</label>
          <input
            type="email"
            autoComplete="email"
            {...register('email')}
            {...fieldProps('email', true)}
            className={inputClass}
          />
          {fieldError('email')}
        </div>
      </div>

      <div className="grid gap-2">
        <label htmlFor={`${uid}-company`} className="text-xs font-medium text-slate-200 uppercase tracking-wider">{t('contact.company')}</label>
        <input
          type="text"
          autoComplete="organization"
          {...register('company')}
          {...fieldProps('company', false)}
          className={inputClass}
        />
        {fieldError('company')}
      </div>

      <div className="grid gap-2">
        <label htmlFor={`${uid}-message`} className="text-xs font-medium text-slate-200 uppercase tracking-wider">{t('contact.message')}</label>
        <textarea
          {...register('message')}
          {...fieldProps('message', true)}
          rows={5}
          className={`${inputClass} resize-none`}
        />
        {fieldError('message')}
      </div>

      <div className="grid">
        {/* aria-disabled namiesto disabled: fokus ostane na tlačidle a čítačka ohlási zmenu textu na „Odosielam…“ */}
        <button
          type="submit"
          aria-disabled={sending || undefined}
          className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold px-6 py-3.5 text-sm hover:from-blue-500 hover:to-indigo-500 transition-all shadow-lg shadow-indigo-500/20 ${sending ? 'opacity-60 cursor-wait' : ''}`}
        >
          {sending ? (
            <svg aria-hidden="true" className="animate-spin motion-reduce:hidden w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : null}
          {sending ? t('contact.sending') : t('contact.submit')}
        </button>

        {/* Jediný živý región, v DOM je od začiatku, aby čítačka obsah ohlásila spoľahlivo. */}
        <div aria-live="polite" aria-atomic="true">
          {result && (
            <div className={`mt-5 rounded-2xl border px-4 py-4 text-sm shadow-lg ${result === 'success' ? 'border-emerald-300/40 bg-emerald-50 text-emerald-900' : 'border-red-300/40 bg-red-50 text-red-900'}`}>
              <div className="flex items-start gap-3">
                <div aria-hidden="true" className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${result === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {result === 'success' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                      <path fillRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7 7a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414L9 11.586l6.296-6.296a1 1 0 0 1 1.408 0Z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.5a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0v-4ZM10 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{result === 'success' ? t('contact.success_title') : t('contact.error_title')}</div>
                  <div className="mt-1 leading-relaxed">
                    {result === 'success' ? t('contact.success') : (
                      <Trans
                        i18nKey="contact.error"
                        values={{ mail: CONTACT_MAIL }}
                        components={{ mail: <a href={`mailto:${CONTACT_MAIL}`} className="underline underline-offset-2" /> }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
